import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, MessageCircle, UserPlus, UserMinus, Trophy, Zap, Users, Heart, Gift, Package, Sparkles, Star, PawPrint, Flag, Ban, ShieldCheck } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { Button } from '../components/ui/button';
import RankMedal from '../components/RankMedal';
import VipBadge from '../components/VipBadge';
import FounderBadge from '../components/FounderBadge';
import RelationshipBadge from '../components/RelationshipBadge';
import ReportUserDialog from '../components/ReportUserDialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { COSMIC_RANKS } from '../mock/mock';

export default function PublicProfile() {
  const { uid } = useParams();
  const nav = useNavigate();
  const { user: me } = useAuth();

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [inventory, setInventory] = useState({ items: [], total_count: 0, total_value: 0, unique_types: 0 });
  const [pets, setPets] = useState([]);
  const [followBusy, setFollowBusy] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [blockOpen, setBlockOpen] = useState(false);
  const [blockStatus, setBlockStatus] = useState({ blocked: false });
  const [blockBusy, setBlockBusy] = useState(false);

  // Pull block status once we know we're looking at someone else's profile.
  useEffect(() => {
    if (!uid || !me || uid === me.user_id) return;
    api.get(`/users/${uid}/block-status`)
      .then(r => setBlockStatus(r.data || { blocked: false }))
      .catch(() => {});
  }, [uid, me]);

  const toggleBlock = async () => {
    if (!uid || blockBusy) return;
    setBlockBusy(true);
    try {
      if (blockStatus.blocked) {
        await api.post(`/users/${uid}/unblock`);
        setBlockStatus({ blocked: false });
        toast.success(`${profile?.displayName || 'User'} unblocked`);
      } else {
        await api.post(`/users/${uid}/block`);
        setBlockStatus({ blocked: true, since: new Date().toISOString() });
        toast.success(`${profile?.displayName || 'User'} blocked — you won't see their content anymore`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update block status');
    } finally {
      setBlockBusy(false);
      setBlockOpen(false);
    }
  };

  // If the URL points at the logged-in user, forward to /app/profile
  useEffect(() => {
    if (me && uid && uid === me.user_id) nav('/app/profile', { replace: true });
  }, [me, uid, nav]);

  useEffect(() => {
    let alive = true;
    setLoading(true); setNotFound(false); setProfile(null);
    api.get(`/users/${uid}`)
      .then(r => { if (alive) setProfile(r.data); })
      .catch(err => { if (alive) { if (err?.response?.status === 404) setNotFound(true); else toast.error('Could not load profile'); } })
      .finally(() => { if (alive) setLoading(false); });
    api.get(`/users/${uid}/gifts-inventory`)
      .then(r => { if (alive) setInventory(r.data); })
      .catch(() => {});
    api.get(`/pets/user/${uid}`)
      .then(r => { if (alive) setPets(Array.isArray(r.data) ? r.data : []); })
      .catch(() => {});
    // Record a "visitor" ping so the visited user sees you in their visitors list
    api.post(`/users/${uid}/view`).catch(() => {});
    return () => { alive = false; };
  }, [uid]);

  const toggleFollow = async () => {
    if (!profile || followBusy) return;
    setFollowBusy(true);
    try {
      if (profile.is_following) {
        await api.post(`/users/${uid}/unfollow`);
        setProfile(p => ({ ...p, is_following: false, followers: Math.max(0, (p.followers || 1) - 1) }));
        toast.success(`Unfollowed ${profile.displayName}`);
      } else {
        await api.post(`/users/${uid}/follow`);
        setProfile(p => ({ ...p, is_following: true, followers: (p.followers || 0) + 1 }));
        toast.success(`Following ${profile.displayName}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update follow');
    } finally {
      setFollowBusy(false);
    }
  };

  const openMessage = () => {
    if (!profile) return;
    // Route to Mail with a `?to=<uid>` deep-link — Messages.jsx picks this up
    // and opens (or starts) the thread with that user, even if no messages
    // have been exchanged yet. Bare `/app/messages/<uid>` would have hit the
    // NotFound catch-all since the route isn't parametric.
    nav(`/app/messages?to=${profile.user_id}`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 text-white/60" data-testid="public-profile-loading">
        <Sparkles className="animate-pulse mr-2" size={18} /> Loading profile…
      </div>
    );
  }
  if (notFound || !profile) {
    return (
      <div className="max-w-md mx-auto text-center py-16" data-testid="public-profile-not-found">
        <div className="text-6xl mb-3">🛸</div>
        <h2 className="font-display font-bold text-2xl">Traveler not found</h2>
        <p className="text-white/60 mt-2 text-sm">This user may have deactivated their account or the link is invalid.</p>
        <Button onClick={() => nav(-1)} className="mt-6 bg-white/10 hover:bg-white/15 text-white border border-white/15 rounded-full"><ArrowLeft size={14} className="mr-1" />Go back</Button>
      </div>
    );
  }

  const rank = COSMIC_RANKS.find(r => r.rank === profile.rank) || COSMIC_RANKS[0];
  const next = COSMIC_RANKS.find(r => r.rank === profile.rank + 1);
  const progress = next ? Math.min(100, ((profile.xp - rank.xp) / Math.max(1, next.xp - rank.xp)) * 100) : 100;

  return (
    <div className="space-y-6" data-testid="public-profile-page">
      {/* Banner + Avatar */}
      <div className="relative rounded-3xl overflow-hidden border border-white/10 bg-[#161622]">
        <div
          className="h-40 sm:h-52 w-full"
          style={{
            background: profile.banner?.startsWith('#')
              ? profile.banner
              : profile.banner_url
                ? `url(${profile.banner_url.startsWith('http') ? profile.banner_url : (process.env.REACT_APP_BACKEND_URL || '') + profile.banner_url}) center/cover no-repeat`
                : profile.banner
                  ? `url(${profile.banner}) center/cover no-repeat`
                  : 'linear-gradient(135deg,#7B2FF7,#00C6FF,#00F5D4)',
          }}
        />
        <button
          onClick={() => nav(-1)}
          className="absolute top-3 left-3 p-2 rounded-full bg-black/40 hover:bg-black/60 backdrop-blur text-white/90"
          aria-label="Go back"
          data-testid="public-profile-back"
        >
          <ArrowLeft size={16} />
        </button>

        <div className="px-4 sm:px-6 pb-5 -mt-14 sm:-mt-16">
          <div className="flex items-end gap-4 flex-wrap">
            <div className="relative">
              <Avatar className="w-24 h-24 sm:w-28 sm:h-28 ring-4 ring-[#0A0A0F]" style={{ boxShadow: `0 0 0 4px ${rank.color}55` }}>
                <AvatarImage src={profile.avatar} />
                <AvatarFallback className="bg-fuchsia-500/40 text-2xl">{profile.displayName?.slice(0,2).toUpperCase()}</AvatarFallback>
              </Avatar>
              <div className="absolute -bottom-2 -right-2 sm:-bottom-3 sm:-right-3">
                <RankMedal rank={profile.rank} size={64} data-testid="public-profile-rank-medal" />
              </div>
            </div>

            <div className="min-w-0 flex-1 pt-2">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="font-display font-bold text-xl sm:text-2xl truncate">{profile.displayName}</h1>
                {profile.flag && <span className="text-lg">{profile.flag}</span>}
                <VipBadge active={profile.is_vip} size="sm" data-testid="public-profile-vip-badge" />
                <FounderBadge active={profile.is_founder} size="md" data-testid="public-profile-founder-badge" />
              </div>
              <div className="mt-1 flex items-center gap-2 text-xs" style={{ color: rank.color }}>
                <Trophy size={12} /> {rank.name} · Rank {profile.rank}
              </div>
              {profile.bio && <p className="text-white/70 text-sm mt-2 max-w-xl whitespace-pre-line">{profile.bio}</p>}
            </div>

            <div className="flex items-center gap-2 pt-2 flex-wrap">
              <Button
                onClick={toggleFollow}
                disabled={followBusy}
                data-testid="public-profile-follow-btn"
                className={profile.is_following
                  ? 'bg-white/10 hover:bg-rose-500/20 text-white border border-white/15 rounded-full'
                  : 'bg-gradient-to-r from-fuchsia-500 to-cyan-500 text-white border-0 rounded-full'}
              >
                {profile.is_following ? <UserMinus size={14} className="mr-1" /> : <UserPlus size={14} className="mr-1" />}
                {profile.is_following ? 'Following' : 'Follow'}
              </Button>
              <Button
                onClick={openMessage}
                data-testid="public-profile-message-btn"
                variant="outline"
                className="border-white/15 bg-white/5 hover:bg-white/10 text-white rounded-full"
              >
                <MessageCircle size={14} className="mr-1" />Message
              </Button>
              <Button
                onClick={() => setReportOpen(true)}
                data-testid="public-profile-report-btn"
                variant="outline"
                className="border-white/15 bg-white/5 hover:bg-rose-500/20 hover:border-rose-400/40 text-white/80 hover:text-white rounded-full"
                title="Report this user"
              >
                <Flag size={14} className="mr-1" />Report
              </Button>
              <Button
                onClick={() => setBlockOpen(true)}
                disabled={blockBusy}
                data-testid="public-profile-block-btn"
                variant="outline"
                className={blockStatus.blocked
                  ? 'border-emerald-400/40 bg-emerald-400/10 hover:bg-emerald-400/20 text-emerald-200 rounded-full'
                  : 'border-white/15 bg-white/5 hover:bg-red-500/20 hover:border-red-400/40 text-white/80 hover:text-white rounded-full'}
                title={blockStatus.blocked ? 'Unblock this user' : 'Block this user'}
              >
                {blockStatus.blocked
                  ? (<><ShieldCheck size={14} className="mr-1" />Blocked</>)
                  : (<><Ban size={14} className="mr-1" />Block</>)}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Report dialog */}
      <ReportUserDialog
        open={reportOpen}
        onOpenChange={setReportOpen}
        target={profile ? { user_id: profile.user_id, displayName: profile.displayName } : null}
        content={{ type: 'user' }}
      />

      {/* Block confirmation */}
      <AlertDialog open={blockOpen} onOpenChange={setBlockOpen}>
        <AlertDialogContent className="bg-[#161622] border-white/10 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display flex items-center gap-2">
              {blockStatus.blocked ? <ShieldCheck size={16} className="text-emerald-300" /> : <Ban size={16} className="text-rose-400" />}
              {blockStatus.blocked ? 'Unblock this user?' : `Block ${profile?.displayName}?`}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-white/70">
              {blockStatus.blocked
                ? "You'll start seeing their messages, feed posts, and profile again. They won't be notified."
                : (
                  <>
                    They won&apos;t be able to message you, and you won&apos;t see their feed posts or profile activity.
                    They won&apos;t be notified.
                    <div className="mt-2 text-[11px] text-white/45">You can unblock anytime from Settings.</div>
                  </>
                )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="block-cancel" className="bg-white/5 border-white/10 text-white hover:bg-white/10">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={toggleBlock}
              data-testid="block-confirm"
              className={blockStatus.blocked
                ? 'bg-emerald-500 hover:bg-emerald-600 text-black border-0'
                : 'bg-rose-500 hover:bg-rose-600 text-white border-0'}
            >
              {blockStatus.blocked ? 'Yes, unblock' : 'Yes, block'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* XP progress */}
      <div className="rounded-2xl p-4 border border-white/10 bg-[#161622]">
        <div className="flex items-center justify-between text-sm">
          <span className="flex items-center gap-1" style={{ color: rank.color }}><Trophy size={13} /> {rank.name}</span>
          <span className="text-white/70 text-xs flex items-center gap-1"><Zap size={11} fill="currentColor" className="text-cyan-300" />{(profile.xp || 0).toLocaleString()}{next ? ` / ${next.xp.toLocaleString()} XP` : ' XP · Max rank'}</span>
          {next && <span className="text-white/50 text-xs">{next.name}</span>}
        </div>
        <div className="mt-2 h-1.5 bg-white/8 rounded-full overflow-hidden">
          <div className="h-full rounded-full" style={{ width: `${progress}%`, background: 'linear-gradient(90deg,#00C6FF,#7B2FF7,#00F5D4)' }} />
        </div>
      </div>

      {profile.relationship && (
        <RelationshipBadge relationship={profile.relationship} isSelf={false} />
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard icon={Users}  label="Followers"  value={profile.followers || 0} color="text-cyan-300" />
        <StatCard icon={Heart}  label="Following"  value={profile.following || 0} color="text-rose-300" />
        <StatCard icon={Gift}   label="Gifts recv" value={inventory.total_count || 0} color="text-fuchsia-300" />
        <StatCard icon={Star}   label="Gift value" value={inventory.total_value || 0} color="text-amber-300" suffix=" ★" />
      </div>

      {/* About */}
      {(profile.country || profile.activity_name || profile.dating_enabled || profile.gender || profile.age) && (
        <div className="rounded-2xl border border-white/10 bg-[#161622] p-4" data-testid="public-profile-about">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles size={14} className="text-cyan-300" />
            <h3 className="font-display font-bold text-sm uppercase tracking-wider text-white/80">About</h3>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
            {profile.country && (
              <InfoRow label="Country" value={<span className="inline-flex items-center gap-1">{profile.flag}{profile.country}</span>} />
            )}
            {profile.activity_name && (
              <InfoRow
                label="Activity tier"
                value={
                  <span className="inline-flex items-center gap-1" style={{ color: profile.activity_color }}>
                    <Zap size={12} fill="currentColor" />{profile.activity_name}
                  </span>
                }
              />
            )}
            {profile.gender && <InfoRow label="Gender" value={<span className="capitalize">{profile.gender}</span>} />}
            {profile.age > 0 && <InfoRow label="Age" value={profile.age} />}
            {profile.looking_for && <InfoRow label="Looking for" value={<span className="capitalize">{profile.looking_for}</span>} />}
            {profile.dating_enabled != null && (
              <InfoRow
                label="Dating"
                value={<span className={profile.dating_enabled ? 'text-fuchsia-300' : 'text-white/40'}>{profile.dating_enabled ? 'Open' : 'Not now'}</span>}
              />
            )}
          </div>
        </div>
      )}

      {/* Pets */}
      <div className="rounded-2xl border border-white/10 bg-[#161622] p-4" data-testid="public-profile-pets">
        <div className="flex items-center gap-2 mb-3">
          <PawPrint size={14} className="text-emerald-300" />
          <h3 className="font-display font-bold text-sm uppercase tracking-wider text-white/80">Pets</h3>
          <span className="ml-auto text-xs text-white/40">{pets.length}</span>
        </div>
        {pets.length ? (
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2.5">
            {pets.slice(0, 12).map(p => (
              <div key={p.id} className="relative rounded-xl overflow-hidden border border-white/8" data-testid={`public-profile-pet-${p.id}`}>
                <div
                  className="aspect-square flex items-center justify-center text-3xl relative"
                  style={{ background: `linear-gradient(135deg, ${p.color || '#4b5563'}55, #0a0a15)` }}
                >
                  {p.image_data_url ? (
                    <img src={p.image_data_url} alt={p.name} className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
                  ) : (
                    <span aria-hidden>{p.emoji || '🐾'}</span>
                  )}
                  <span
                    className="absolute top-1 right-1 text-[9px] px-1.5 py-0.5 rounded-full font-semibold uppercase tracking-wider bg-black/50 backdrop-blur"
                    style={{ color: p.color || '#F8FAFC' }}
                  >
                    {p.tag || p.rarity}
                  </span>
                </div>
                <div className="p-2 text-center">
                  <div className="text-[11px] font-medium truncate">{p.name}</div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-xs text-white/50 py-4 text-center">No pets adopted yet.</div>
        )}
      </div>

      {/* Gift inventory */}
      <div className="rounded-2xl border border-white/10 bg-[#161622] p-4">
        <div className="flex items-center gap-2 mb-3">
          <Package size={14} className="text-fuchsia-300" />
          <h3 className="font-display font-bold text-sm uppercase tracking-wider text-white/80">Gift inventory</h3>
          <span className="ml-auto text-xs text-white/40">{inventory.unique_types || 0} unique</span>
        </div>
        {inventory.items?.length ? (
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2.5">
            {inventory.items.slice(0, 12).map(g => (
              <div key={g.gift_id} className="relative rounded-xl p-3 bg-white/5 border border-white/5 text-center" data-testid={`public-profile-gift-${g.gift_id}`}>
                <span className="absolute top-1 right-1 text-[10px] px-1.5 py-0.5 rounded-full bg-fuchsia-500/25 text-fuchsia-200">x{g.count}</span>
                <div className="text-2xl">{g.emoji || '🎁'}</div>
                <div className="text-[11px] text-white/70 mt-1 truncate">{g.name}</div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-xs text-white/50 py-4 text-center">No gifts yet.</div>
        )}
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color, suffix = '' }) {
  return (
    <div className="rounded-2xl p-3 border border-white/10 bg-[#161622]">
      <div className={`flex items-center gap-1.5 text-[11px] uppercase tracking-wider ${color}`}>
        <Icon size={11} /> {label}
      </div>
      <div className="mt-1 font-display font-bold text-xl">{value.toLocaleString?.() || value}{suffix}</div>
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-white/40">{label}</div>
      <div className="text-sm text-white/90 mt-0.5">{value}</div>
    </div>
  );
}
